import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Receipt as ReceiptIcon, Plus, RefreshCw, Send, Download,
  Trash2, FileText, DollarSign, Search, MessageCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import CustomerCombobox from "../components/CustomerCombobox";

const METHOD_OPTIONS = ["Cash", "OMT", "Whish", "BoB", "Bank transfer", "USDT (crypto)", "Other"];
const QUICK_LABELS = [
  "Deposit for new machine",
  "Hosting fee — prior month",
  "Partial payment",
  "Full settlement",
  "Machine purchase",
  "Repair fee",
];

const Receipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState("");

  // Form state
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customer_id: "",
    amount: "",
    label: "",
    method: "Cash",
    date: today,
    note: "",
    send_whatsapp: true,
    message: "",
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/receipts`),
        axios.get(`${API}/customers`),
      ]);
      setReceipts(r1.data || []);
      setCustomers((r2.data || []).slice().sort((a,b) => (a.name||'').localeCompare(b.name||'', undefined, {sensitivity:'base'})));
    } catch (e) {
      toast.error("Failed to load receipts");
    }
    setLoading(false);
  };

  const resetForm = () => setForm({
    customer_id: "", amount: "", label: "", method: "Cash",
    date: today, note: "", send_whatsapp: true, message: "",
  });

  const submit = async () => {
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    if (!form.label.trim()) { toast.error("Add a label (what is this payment for?)"); return; }

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/receipts`, {
        customer_id: form.customer_id,
        amount: amt,
        label: form.label.trim(),
        method: form.method,
        date: form.date,
        note: form.note.trim(),
        send_whatsapp: form.send_whatsapp,
        message: form.message.trim() || undefined,
      });
      const method = r.data?.method;
      if (r.data?.warning) {
        toast.warning(r.data.warning, { duration: 9000 });
      } else if (method === "text_with_link") {
        toast.warning(
          "⚠️ Receipt saved but PDF couldn't be delivered — sent as text+link only. Use the Manual button to send the PDF.",
          { duration: 9000 }
        );
      } else if (form.send_whatsapp) {
        toast.success(`Receipt sent on WhatsApp ✓${method ? ` (${method})` : ""}`);
      } else {
        toast.success("Receipt created ✓");
      }
      setShowForm(false);
      resetForm();
      setReceipts(prev => [r.data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create receipt");
    }
    setSubmitting(false);
  };

  const resend = async (id) => {
    try {
      const r = await axios.post(`${API}/receipts/${id}/send`, {});
      if (r.data?.warning) {
        toast.warning(r.data.warning, { duration: 8000 });
      } else {
        toast.success("Receipt resent on WhatsApp ✓");
      }
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const openWhatsAppManual = (receipt) => {
    const cust = customers.find(c => c.id === receipt.customer_id);
    const phone = (cust?.phone || "").replace(/[^\d]/g, "");
    if (!phone) { toast.error("No phone for this customer"); return; }
    const msg = encodeURIComponent(
      `📄 Receipt #${receipt.receipt_no}\n${receipt.label}\nAmount: $${Number(receipt.amount).toFixed(2)}\nMethod: ${receipt.method}\n\nThank you!`
    );
    // Open WhatsApp Web/app with the pre-filled message; admin attaches the downloaded PDF.
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
    // Also pop the PDF in a new tab so admin can save & drag it in.
    window.open(`${API}/receipts/${receipt.id}/pdf`, "_blank");
    toast.info("Opened WhatsApp + PDF. Save the PDF then attach it in WhatsApp.", { duration: 6000 });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this receipt from history? The PDF link will no longer work.")) return;
    try {
      await axios.delete(`${API}/receipts/${id}`);
      setReceipts(prev => prev.filter(r => r.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const openPdf = async (id) => {
    try {
      // Fetch as blob so we don't trigger Chrome's cross-origin safety warning
      // (frontend on wkbeast.org, backend on onrender.com).
      const resp = await axios.get(`${API}/receipts/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Free the URL after 1 minute
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Failed to load PDF");
    }
  };

  const filtered = query.trim()
    ? receipts.filter(r => {
        const q = query.toLowerCase();
        return (r.customer_name || "").toLowerCase().includes(q)
          || (r.label || "").toLowerCase().includes(q)
          || (r.receipt_no || "").toLowerCase().includes(q)
          || (r.method || "").toLowerCase().includes(q);
      })
    : receipts;

  const totalAmount = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="receipts-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ReceiptIcon size={24} className="text-[#00E054]" /> Receipts
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Issue ad-hoc receipts for deposits, prior months, partial payments — auto-sends a styled PDF on WhatsApp.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#27272A] text-gray-300"
            onClick={fetchAll}
            data-testid="btn-refresh-receipts"
          >
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button
            className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
            onClick={() => setShowForm(true)}
            data-testid="btn-new-receipt"
          >
            <Plus size={14} className="mr-1" /> New Receipt
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
          <p className="text-xs text-gray-500">Total receipts</p>
          <p className="text-2xl font-bold text-white mt-1">{filtered.length}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
          <p className="text-xs text-gray-500">Total received</p>
          <p className="text-2xl font-bold text-[#00E054] mt-1">${totalAmount.toFixed(2)}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
          <p className="text-xs text-gray-500">Sent on WhatsApp</p>
          <p className="text-2xl font-bold text-cyan-400 mt-1">
            {filtered.filter(r => r.sent_via_whatsapp).length}/{filtered.length}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#00E054]"
          placeholder="Search by name, label, receipt #, method…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="receipts-search"
        />
      </div>

      {/* Table */}
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl overflow-hidden">
        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-10 text-gray-500">
            {query ? "No matches" : "No receipts yet. Click \"New Receipt\" to issue one."}
          </p>
        ) : (
          <>
            {/* Desktop / tablet: full table */}
            <div className="data-table-wrap overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#18181B] text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Receipt #</th>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Label</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Method</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-center px-4 py-3">WA Sent</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className="border-t border-[#27272A] hover:bg-[#0F0F11]" data-testid={`receipt-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-300">{r.receipt_no}</td>
                      <td className="px-4 py-3 text-white">{r.customer_name || "—"}</td>
                      <td className="px-4 py-3 text-gray-300">{r.label}</td>
                      <td className="px-4 py-3 text-right text-[#00E054] font-bold">${Number(r.amount).toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-300">{r.method}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{(r.date || r.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-center">
                        {r.sent_via_whatsapp
                          ? <span className="text-[#00E054]">✓</span>
                          : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="border-[#27272A] text-xs h-7" onClick={() => openPdf(r.id)} data-testid={`btn-view-${r.id}`}>
                            <FileText size={12} className="mr-1" /> PDF
                          </Button>
                          <Button size="sm" variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs h-7" onClick={() => resend(r.id)} data-testid={`btn-resend-${r.id}`}>
                            <Send size={12} className="mr-1" /> Resend
                          </Button>
                          <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 text-xs h-7" title="If auto-send fails: opens WhatsApp + downloads PDF for manual send" onClick={() => openWhatsAppManual(r)} data-testid={`btn-wa-manual-${r.id}`}>
                            <MessageCircle size={12} className="mr-1" /> Manual
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs h-7 px-2" onClick={() => remove(r.id)} data-testid={`btn-del-${r.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: one card per receipt */}
            <div className="data-cards p-3">
              {filtered.map(r => (
                <div key={r.id} className="data-card" data-testid={`receipt-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-white font-semibold truncate">{r.customer_name || "—"}</p>
                      <p className="text-xs text-gray-400 truncate">{r.label}</p>
                    </div>
                    <span className="text-[#00E054] font-bold shrink-0">${Number(r.amount).toFixed(2)}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Receipt #</span>
                    <span className="font-mono text-xs text-gray-300">{r.receipt_no}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Method</span>
                    <span className="text-gray-300 text-sm">{r.method}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Date</span>
                    <span className="text-gray-400 text-xs">{(r.date || r.created_at || "").slice(0, 10)}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">WhatsApp sent</span>
                    {r.sent_via_whatsapp
                      ? <span className="text-[#00E054] text-sm">✓ Sent</span>
                      : <span className="text-gray-600 text-sm">— Not sent</span>}
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 mt-3">
                    <Button size="sm" variant="outline" className="border-[#27272A] text-xs h-8 px-1" onClick={() => openPdf(r.id)} data-testid={`btn-view-mobile-${r.id}`}>
                      <FileText size={12} />
                    </Button>
                    <Button size="sm" variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs h-8 px-1" onClick={() => resend(r.id)} data-testid={`btn-resend-mobile-${r.id}`}>
                      <Send size={12} />
                    </Button>
                    <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 text-xs h-8 px-1" title="Manual send" onClick={() => openWhatsAppManual(r)} data-testid={`btn-wa-manual-mobile-${r.id}`}>
                      <MessageCircle size={12} />
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs h-8 px-1" onClick={() => remove(r.id)} data-testid={`btn-del-mobile-${r.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* New Receipt Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="receipt-modal">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <ReceiptIcon size={18} className="text-[#00E054]" /> Issue Receipt
              </h2>
              <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => { setShowForm(false); resetForm(); }}>✕</Button>
            </div>
            <div className="p-5 space-y-4">
              {/* Customer */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Customer *</label>
                <CustomerCombobox
                  customers={customers}
                  value={form.customer_id}
                  onChange={(id) => setForm({ ...form, customer_id: id })}
                  placeholder="— pick a customer —"
                  testid="receipt-customer-select"
                />
              </div>

              {/* Amount + Method */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Amount (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00E054]"
                    placeholder="500.00"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="receipt-amount"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Payment method</label>
                  <select
                    className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00E054]"
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                    data-testid="receipt-method"
                  >
                    {METHOD_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {/* Label + quick chips */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Label (what is this for?) *</label>
                <input
                  className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00E054]"
                  placeholder="e.g. Deposit for Machine A, Hosting fee December 2025"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  data-testid="receipt-label"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {QUICK_LABELS.map(q => (
                    <button
                      key={q}
                      onClick={() => setForm(f => ({ ...f, label: q }))}
                      className="text-[10px] px-2 py-1 rounded-full bg-[#18181B] border border-[#27272A] text-gray-400 hover:text-[#00E054] hover:border-[#00E054]/50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Date received</label>
                <input
                  type="date"
                  className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00E054]"
                  value={form.date.slice(0, 10)}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="receipt-date"
                />
              </div>

              {/* Note (optional) */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Note (optional, appears on the PDF)</label>
                <textarea
                  className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00E054] min-h-[60px]"
                  placeholder="e.g. Covers hosting for Dec 2025 + Jan 2026"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  data-testid="receipt-note"
                />
              </div>

              {/* WhatsApp toggle */}
              <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.send_whatsapp}
                    onChange={(e) => setForm({ ...form, send_whatsapp: e.target.checked })}
                    className="accent-[#00E054] w-4 h-4"
                    data-testid="receipt-send-whatsapp"
                  />
                  <span className="text-sm text-white">Send PDF to customer on WhatsApp now</span>
                </label>
                {form.send_whatsapp && (
                  <div>
                    <label className="text-[10px] text-gray-500 mb-1 block">Custom WhatsApp message (optional — leave empty for default)</label>
                    <textarea
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[#00E054] min-h-[50px]"
                      placeholder="Default: '📄 Receipt #ABC123 · $500.00 · Thank you!'"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="receipt-wa-message"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-[#27272A] p-4 flex justify-end gap-2">
              <Button variant="outline" className="border-[#27272A] text-gray-300" onClick={() => { setShowForm(false); resetForm(); }}>
                Cancel
              </Button>
              <Button
                className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                onClick={submit}
                disabled={submitting}
                data-testid="btn-submit-receipt"
              >
                {submitting ? "Working…" : (form.send_whatsapp ? "Issue & Send on WhatsApp" : "Issue Receipt")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;
